import { HeapPHIDecorator } from "@app/components/analytics";
import React, {
  FC,
  ReactElement,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { defineMessages, useIntl } from "react-intl";
import {
  ClearErrors,
  ClearValidationError,
  SetValidationError,
} from "@natera/platform/lib/hooks/useError";
import { Button, Form } from "@natera/form";
import {
  ConfigContext,
  ErrorContext,
  NotificationContext,
} from "@app/provider";
import {
  FormField,
  PasswordField,
  EmailInputWithDomainSuggestions,
} from "@app/components";
import { useNavigate, useLocation } from "react-router-dom";
import validateEmail from "../../../utils/validateEmail";

import "./loginForm.scss";

const messages = defineMessages({
  loginFormEmail: {
    id: "loginFormEmail",
    defaultMessage: "Email Address",
  },
  loginFormPassword: {
    id: "loginFormPassword",
    defaultMessage: "Password",
  },
  loginFormEmailInvalid: {
    id: "loginFormEmailInvalid",
    defaultMessage: "Please enter a valid email address. (username@domain)",
  },
  loginFormFieldEmpty: {
    id: "loginFormFieldEmpty",
    defaultMessage: "Please fill in this field",
  },
  loginFormButton: {
    id: "loginFormButton",
    defaultMessage: "Log in",
  },
  loginFormEmailPlaceholder: {
    id: "loginFormEmailPlaceholder",
    defaultMessage: "Enter Your Email Address",
  },
  loginFormEmailFormat: {
    id: "loginFormEmailFormat",
    defaultMessage: "Email format (username@domain)",
  },
  loginFormEmailAlreadyUsed: {
    id: "loginFormEmailAlreadyUsed",
    defaultMessage: "This email address is already in use",
  },
  loginFormPasswordPlaceholder: {
    id: "loginFormPasswordPlaceholder",
    defaultMessage: "Enter Your Password",
  },
});

interface LoginFormProps {
  setValidationError: SetValidationError;
  clearValidationError: ClearValidationError;
  clearErrors: ClearErrors;
  onSubmit: (props: { email: string; password: string }) => void;
  isLoading: boolean;
  prefilledEmail?: string;
}

const LoginForm: FC<LoginFormProps> = ({
  setValidationError,
  clearValidationError,
  clearErrors,
  onSubmit,
  isLoading,
  prefilledEmail,
}) => {
  const intl = useIntl();
  const { getValidationError } = useContext(ErrorContext);
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { clear } = useContext(NotificationContext);
  const { config } = React.useContext(ConfigContext);

  const usePlusSignInEmailAddress =
    config.test.usePlusSignInEmailAddress.enabled;

  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const [email, setEmail] = useState(prefilledEmail || "");
  const [emailTooltipIsOpened, setEmailTooltipIsOpened] = useState(false);

  useEffect(() => {
    clearErrors();

    return () => {
      clearErrors();
    };
  }, []);

  useEffect(() => {
    if (prefilledEmail) {
      setEmail(prefilledEmail);
    }
  }, [prefilledEmail]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    clearErrors();
    if (searchParams.get("code")) {
      clear();
      navigate(pathname, { replace: true });
    }

    const formData = new FormData(e.currentTarget);

    const emailValue = email.trim();
    const password = formData.get("password")?.toString();

    if (!emailValue) {
      setValidationError(
        "email",
        intl.formatMessage(messages.loginFormFieldEmpty)
      );

      return;
    }

    if (!password) {
      setValidationError(
        "password",
        intl.formatMessage(messages.loginFormFieldEmpty)
      );

      return;
    }

    if (!validateEmail(emailValue, usePlusSignInEmailAddress)) {
      setValidationError(
        "email",
        intl.formatMessage(messages.loginFormEmailInvalid)
      );

      return;
    }

    if (!isLoading) {
      onSubmit({ email: emailValue, password });
    }
  };

  const handleEmailFocus = () => {
    clearValidationError("email");
    setEmailTooltipIsOpened(true);
  };

  const handleEmailBlur = () => {
    setEmailTooltipIsOpened(false);
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    clearValidationError("email");
  };

  const handlePasswordFocus = () => {
    clearValidationError("password");
  };

  const emailAddressFormFieldElement: ReactElement = (
    <FormField
      className="email-field"
      label={intl.formatMessage(messages.loginFormEmail)}
      required
      error={getValidationError("email")}
      htmlFor="email"
      withPadding
    >
      <HeapPHIDecorator protectAttr={["value"]} noWrap>
        <EmailInputWithDomainSuggestions
          placeholder={intl.formatMessage(messages.loginFormEmailPlaceholder)}
          outline
          id="email"
          name="email"
          required
          onFocus={handleEmailFocus}
          onBlur={handleEmailBlur}
          value={email}
          onChange={handleEmailChange}
          autoComplete="email"
          tooltipContent={intl.formatMessage(messages.loginFormEmailFormat)}
          showTooltip={emailTooltipIsOpened}
          error={getValidationError("email")}
        />
      </HeapPHIDecorator>
    </FormField>
  );

  const passwordFormFieldElement: ReactElement = (
    <FormField
      label={intl.formatMessage(messages.loginFormPassword)}
      required
      error={getValidationError("password")}
      htmlFor="password"
      withPadding
    >
      <PasswordField
        placeholder={intl.formatMessage(messages.loginFormPasswordPlaceholder)}
        outline
        id="password"
        name="password"
        required
        onFocus={handlePasswordFocus}
        autoComplete="current-password"
      />
    </FormField>
  );

  return (
    <Form className="login-form" onSubmit={handleSubmit} noValidate>
      {emailAddressFormFieldElement}
      {passwordFormFieldElement}
      <Button type="submit" unelevated>
        {intl.formatMessage(messages.loginFormButton)}
      </Button>
    </Form>
  );
};

export default LoginForm;
